/* ------------------------------------------------------------------
   Smart offers behaviour.
   Cart: applies a discount code to the CART itself via
   POST /cart/update.js { discount } — the same mechanism the checkout
   reads — so the cart total, the footer discount line and checkout
   all reflect the code instantly, no refresh. The request bundles
   Shopify section rendering, so the panel and totals re-render from
   one round trip. The biggest general (non-first-order) code is
   auto-applied once; the shopper can switch or remove freely.
   PDP: copy-to-clipboard for the offer code chips.
   Progressive enhancement: without JS the panel still lists codes and
   they can be typed at checkout. Only a "shopper removed the code"
   flag is stored, per tab session — no personal data.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var DISMISS_KEY = 'tapi:offer-dismissed';
  var MANUAL_KEY = 'tapi:offer-manual';
  var busy = false;
  var autoTried = false;

  function panel() {
    return document.querySelector('[data-tapi-offers]');
  }

  function dismissed() {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch (e) { return false; }
  }

  function setDismissed(on) {
    try {
      if (on) sessionStorage.setItem(DISMISS_KEY, '1');
      else sessionStorage.removeItem(DISMISS_KEY);
    } catch (e) { /* private mode */ }
  }

  // Codes the recovery flow issues, which carry the brand prefix and are
  // single use. No public code uses this prefix; the live ones are BUY2,
  // BUY3, WELCOME150 and OFF20. Kept in step with cartRecovery.codePrefix in
  // the wa-automation service.
  var RECOVERY_CODE = /^MORPAANKH[A-Z]*\d+$/;

  // The code the shopper picked by hand this session. A deliberate choice is
  // respected; a stale or link-supplied code is not, so it can be upgraded to
  // the biggest saving.
  function manualChoice() {
    try { return sessionStorage.getItem(MANUAL_KEY) || ''; } catch (e) { return ''; }
  }

  function setManual(code) {
    try {
      if (code) sessionStorage.setItem(MANUAL_KEY, code);
      else sessionStorage.removeItem(MANUAL_KEY);
    } catch (e) { /* private mode */ }
  }

  function sectionIds() {
    var ids = [];
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    if (items && items.dataset.id) ids.push(items.dataset.id);
    if (footer && footer.dataset.id) ids.push(footer.dataset.id);
    return ids;
  }

  function swapSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var html = sections[id];
      var host = document.getElementById('shopify-section-' + id);
      if (!html || !host) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('.js-contents');
      var live = host.querySelector('.js-contents');
      if (fresh && live) live.innerHTML = fresh.innerHTML;
      // Sticky desktop order summary sits outside .js-contents in the items
      // section; swap it too or its totals lag behind the applied code.
      var freshAside = doc.querySelector('.tapi-cart-aside');
      var liveAside = host.querySelector('.tapi-cart-aside');
      if (freshAside && liveAside) liveAside.innerHTML = freshAside.innerHTML;
      // Mobile sticky checkout bar (amount + savings badge) also sits outside
      // .js-contents; swap it so a promo-code switch updates it live too.
      var freshBar = doc.querySelector('.tapi-cart-stickybar');
      var liveBar = host.querySelector('.tapi-cart-stickybar');
      if (freshBar && liveBar) liveBar.innerHTML = freshBar.innerHTML;
    });
  }

  function setStatus(msg, isError) {
    var p = panel();
    if (!p) return;
    var s = p.querySelector('[data-tapi-offers-status]');
    if (!s) return;
    s.textContent = msg || '';
    s.classList.toggle('is-error', !!isError);
  }

  function rupees(paise) {
    return 'Rs. ' + (paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function postDiscount(code, opts) {
    if (busy) return;
    busy = true;
    opts = opts || {};
    var prev = null;
    var p = panel();
    if (p) prev = p.getAttribute('data-applied-code') || '';
    var body = { discount: code };
    var ids = sectionIds();
    if (ids.length) body.sections = ids.join(',');
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var entry = (cart.discount_codes || [])[0];
        var applicable = code === '' || (entry && entry.applicable === true);
        swapSections(cart.sections);
        if (applicable) {
          if (code !== '') {
            try { if (window.clarity) window.clarity('set', 'discount_applied', code); } catch (e) { /* analytics must never break the cart */ }
          }
          if (!opts.silent) {
            if (code === '') setStatus('Code removed.');
            else setStatus('Code ' + code + ' applied. You’re saving ' + rupees(cart.total_discount) + ' on this order.');
          }
          busy = false;
          if (opts.after) opts.after();
        } else {
          // Structural miss (cart changed under us): put things back, then
          // say why — after the restore's own section swap, or the message
          // would be wiped along with the panel HTML.
          busy = false;
          postDiscount(prev || '', {
            silent: true,
            after: function () {
              setStatus('Code ' + code + ' does not fit this cart yet.', true);
            }
          });
        }
      })
      .catch(function () {
        busy = false;
        setStatus('Could not update the code. Please try again.', true);
      });
  }

  function initCart() {
    var p = panel();
    if (!p || autoTried) return;
    var applied = p.getAttribute('data-applied-code') || '';
    // Apply whichever enabled code saves the most on THIS cart. best-code
    // already accounts for first-order eligibility (the server hides
    // first-order offers like WELCOME150 from logged-in returning customers),
    // so new shoppers get the welcome amount and multi-piece carts get the
    // bigger quantity break. Fall back to the best general code if best-code
    // is ever blank. Checkout stays the final enforcer of one-use-per-customer.
    var best = p.getAttribute('data-best-code') || p.getAttribute('data-best-general') || '';
    if (best === '') return;
    if (dismissed()) return;                 // shopper opted out this session
    if (applied === best) return;            // already on the biggest saving
    // A private cart-recovery code is NOT a "stale or link-supplied lesser
    // code" to be upgraded. The recovery flow only ever issues one that beats
    // the best public code, and only after checking, so replacing it would
    // quietly hand the shopper less than the message they are holding
    // promised. Treated exactly like a code chosen by hand.
    if (RECOVERY_CODE.test(applied)) return;
    // Respect a code the shopper chose by hand; otherwise apply the best, which
    // also UPGRADES a stale or link-supplied lesser code (e.g. someone lands on
    // WELCOME150, adds two pieces, and should get the larger BUY2 instead).
    if (applied !== '' && manualChoice() === applied) return;
    autoTried = true;
    postDiscount(best, { silent: true });
  }

  function copyChip(btn) {
    var code = btn.getAttribute('data-tapi-copy');
    if (!code) return;
    var done = function () {
      btn.classList.add('is-copied');
      var lbl = btn.querySelector('[data-copy-label]');
      var prev = lbl ? lbl.textContent : '';
      if (lbl) lbl.textContent = 'Copied \u2713';
      window.setTimeout(function () {
        btn.classList.remove('is-copied');
        if (lbl) lbl.textContent = prev;
      }, 2000);
    };
    // The old in-app browsers a lot of this traffic arrives in (Instagram,
    // Facebook) DO expose navigator.clipboard, then reject the write with
    // NotAllowedError. Treating only a MISSING API as "use the old way" left
    // those shoppers tapping Copy and getting nothing back, so a rejection
    // falls through to the same fallback.
    var legacy = function () {
      var ta = document.createElement('textarea');
      ta.value = code;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, code.length);   // iOS ignores select() on its own
      try { if (document.execCommand('copy')) done(); } catch (e) { /* no-op */ }
      document.body.removeChild(ta);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done, legacy);
    } else {
      legacy();
    }
  }

  document.addEventListener('click', function (e) {
    var apply = e.target.closest('[data-tapi-offer-apply]');
    if (apply) {
      setDismissed(false);
      var chosen = apply.getAttribute('data-code');
      setManual(chosen);
      postDiscount(chosen);
      return;
    }
    var remove = e.target.closest('[data-tapi-offer-remove]');
    if (remove) {
      setDismissed(true);
      setManual('');
      postDiscount('');
      return;
    }
    var chip = e.target.closest('[data-tapi-copy]');
    if (chip) copyChip(chip);
  });

  function boot() {
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
